import React, { useState, useEffect } from 'react';
import {
  FaDollarSign,
  FaCreditCard,
  FaFileInvoice,
  FaCalendar,
  FaCheckCircle,
  FaTimes,
  FaClock,
  FaExclamationTriangle,
  FaPlay,
  FaPause,
  FaEye,
  FaDownload,
  FaRedo,
  FaSearch,
  FaFilter,
  FaChartLine,
  FaUsers,
  FaWifi,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaHistory,
  FaCog,
  FaBell,
  FaPaperPlane,
  FaBan
} from 'react-icons/fa';
import { API_ENDPOINTS, API_CONFIG } from '../../config/api';
import { useAdminAuth } from '../context/AdminAuthContext';
import ProfessionalInvoiceTemplate from '../../customer-portal/ProfessionalInvoiceTemplate';
import CsvExportButton from './customers/CsvExportButton';
import AdminBillingSummary from './AdminBillingSummary';

const BillingManagement = () => {
  const { token } = useAdminAuth();
  const [customers, setCustomers] = useState([]);

  // Helper function to get headers with auth token
  // Note: HTTP API JWT authorizer requires Bearer prefix
  const getHeaders = () => {
    const headers = {
      'Content-Type': 'application/json'
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  };

  // Debug: Log the API endpoint being used
  useEffect(() => {
    console.log('Billing Management API Endpoint:', API_ENDPOINTS.ADMIN_BILLING_MANAGEMENT);
    console.log('Full URL breakdown:', {
      base: API_CONFIG?.BILLING_MANAGEMENT,
      path: '/Billing-Management',
      full: API_ENDPOINTS.ADMIN_BILLING_MANAGEMENT
    });
  }, []);
  const [billingStats, setBillingStats] = useState({
    totalRevenue: 0,
    monthlyRevenue: 0,
    ddSuccessRate: 0,
    activeServices: 0,
    pendingInvoices: 0,
    failedPayments: 0,
    totalCustomers: 0
  });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [billingHistory, setBillingHistory] = useState([]);
  const [recentBillingRuns, setRecentBillingRuns] = useState([]);
  const [sendingInvoice, setSendingInvoice] = useState(null); // Track which invoice is being sent
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [paymentMethodActionLoading, setPaymentMethodActionLoading] = useState(null);
  const [retryPaymentLoading, setRetryPaymentLoading] = useState(null);
  const [voidInvoiceLoading, setVoidInvoiceLoading] = useState(null);
  // Confirm gate for "Run Full Billing" — admin must type the literal string
  // to proceed. Stops accidental clicks from charging every customer.
  const [showFullRunConfirm, setShowFullRunConfirm] = useState(false);
  const [fullRunConfirmText, setFullRunConfirmText] = useState('');
  // Raw invoice list (cached from loadBillingData) so the Export CSV button
  // can render without re-fetching. Powers BAS-prep transaction dumps.
  const [allInvoices, setAllInvoices] = useState([]);
  // Per-customer billing summary modal — comprehensive view combining
  // outstanding / next charge / promo / 12mo chart / LTV / churn / failures.
  const [summaryCustomer, setSummaryCustomer] = useState(null);

  useEffect(() => {
    if (!token) return;
    loadBillingData();
    loadBillingHistory();
  }, [token]);

  const loadBillingData = async () => {
    try {
      setLoading(true);

      // 1. Load customers
      const customersResponse = await fetch(API_ENDPOINTS.ADMIN_GET_CUSTOMERS, {
        method: 'GET',
        headers: getHeaders()
      });

      if (!customersResponse.ok) throw new Error(`HTTP error! status: ${customersResponse.status}`);
      const customersData = await customersResponse.json();

      // 2. Load ALL invoices (bulk) for aggregation
      // We'll use a larger limit to get a good dataset for stats
      const invoicesResponse = await fetch(API_ENDPOINTS.ADMIN_BILLING_MANAGEMENT, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          action: 'getAllInvoices',
          limit: 1000 // Fetch enough to cover recent history
        })
      });

      let allInvoices = [];
      if (invoicesResponse.ok) {
        const invoicesData = await invoicesResponse.json();
        if (invoicesData.success && invoicesData.invoices) {
          allInvoices = invoicesData.invoices;
        }
      }
      // Stash for CSV export — keeps the button render-only (no async on click).
      setAllInvoices(allInvoices);

      if (customersData.success && customersData.customers) {
        // 3. Aggregate data in memory.
        // Join key set: current email + originalEmail (set on Cancel Service
        // approval when we rename email → "cancelled+ts+original"). Without
        // originalEmail in the set, cancelled customers' invoice history
        // would silently vanish from the admin view.
        const customersWithBilling = customersData.customers.map(customer => {
          const aliases = new Set();
          if (customer.email) aliases.add(customer.email.trim().toLowerCase());
          if (customer.originalEmail) aliases.add(customer.originalEmail.trim().toLowerCase());

          const customerInvoices = aliases.size === 0 ? [] : allInvoices.filter(inv => {
            const invCustomerId = (inv.customerId || '').trim().toLowerCase();
            const invCustomerEmail = (inv.customerEmail || '').trim().toLowerCase();
            return aliases.has(invCustomerId) || aliases.has(invCustomerEmail);
          });

          // Calculate stats
          const successfulPayments = customerInvoices.filter(inv => inv.status === 'paid').length;
          const failedPayments = customerInvoices.filter(inv => inv.status === 'failed').length;

          let lastPaymentAmount = 0;
          let lastBillingDate = null;

          // Sort by date desc to get latest
          customerInvoices.sort((a, b) => new Date(b.invoiceDate || b.createdAt) - new Date(a.invoiceDate || a.createdAt));

          if (customerInvoices.length > 0) {
            const latest = customerInvoices[0];
            lastPaymentAmount = parseFloat(latest.totalAmount || latest.amount || 0);
            lastBillingDate = latest.invoiceDate || latest.dueDate || latest.createdAt;
          }

          return {
            ...customer,
            lastBillingDate: lastBillingDate || customer.lastBillingDate || null,
            nextBillingDate: customer.nextBillingDate || null,
            paymentMethodType: customer.defaultPaymentMethodType || (customer.defaultPaymentMethodId ? 'saved' : (customer.stripeCustomerId ? 'stripe' : 'none')),
            paymentMethodStatus: 'active', // Assumed active if they exist
            totalInvoices: customerInvoices.length,
            successfulPayments,
            failedPayments,
            lastPaymentAmount,
            billingStatus: customer.billingStatus || customer.status || 'active'
          };
        });

        setCustomers(customersWithBilling);

        // 4. Calculate Billing Stats
        // Try real API first
        try {
          const statsResponse = await fetch(API_ENDPOINTS.ADMIN_BILLING_MANAGEMENT, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify({ action: 'getBillingStats' })
          });

          if (statsResponse.ok) {
            const statsData = await statsResponse.json();
            if (statsData.success && statsData.stats) {
              const apiStats = statsData.stats;
              setBillingStats({
                totalRevenue: apiStats.totalRevenue || 0,
                monthlyRevenue: apiStats.monthlyBreakdown ?
                  Object.values(apiStats.monthlyBreakdown).reduce((sum, m) => sum + (m.revenue || 0), 0) : 0,
                ddSuccessRate: apiStats.successRate || 0,
                activeServices: customersWithBilling.filter(c => c.status === 'active').length,
                pendingInvoices: apiStats.pendingCount || 0,
                failedPayments: apiStats.failedCount || 0,
                totalCustomers: customersWithBilling.length
              });
            } else {
              setBillingStats(calculateBillingStats(customersWithBilling));
            }
          } else {
            setBillingStats(calculateBillingStats(customersWithBilling));
          }
        } catch (e) {
          console.warn('Stats API failed, using local calc', e);
          setBillingStats(calculateBillingStats(customersWithBilling));
        }
      }
    } catch (error) {
      console.error('Error loading billing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateBillingStats = (customers) => {
    const totalRevenue = customers.reduce((sum, customer) => sum + (customer.monthlyFee || 0), 0);
    const monthlyRevenue = customers
      .filter(c => c.billingStatus === 'active')
      .reduce((sum, customer) => sum + (customer.monthlyFee || 0), 0);

    const totalPayments = customers.reduce((sum, customer) => sum + (customer.successfulPayments || 0), 0);
    const totalFailed = customers.reduce((sum, customer) => sum + (customer.failedPayments || 0), 0);
    const ddSuccessRate = totalPayments + totalFailed > 0 ? (totalPayments / (totalPayments + totalFailed)) * 100 : 0;

    const activeServices = customers.filter(c => c.billingStatus === 'active').length;
    const pendingInvoices = customers.filter(c => c.billingStatus === 'pending').length;
    const failedPayments = customers.filter(c => c.billingStatus === 'failed').length;

    return {
      totalRevenue,
      monthlyRevenue,
      ddSuccessRate,
      activeServices,
      pendingInvoices,
      failedPayments,
      totalCustomers: customers.length
    };
  };

  const loadBillingHistory = async () => {
    try {
      // Load real billing history from API
      if (selectedCustomer) {
        const historyResponse = await fetch(API_ENDPOINTS.ADMIN_BILLING_MANAGEMENT, {
          method: 'POST',
          headers: getHeaders(),
          body: JSON.stringify({
            action: 'getBillingHistory',
            customerEmail: selectedCustomer.email,
            email: selectedCustomer.email,
            limit: 50
          })
        });

        if (historyResponse.ok) {
          const historyData = await historyResponse.json();
          if (historyData.success && historyData.billingHistory) {
            // Transform API response to match component format
            const transformedHistory = historyData.billingHistory.map((invoice) => ({
              id: invoice.invoiceId || invoice.id,
              customerId: invoice.customerId,
              customerName: historyData.customer?.fullName || selectedCustomer.fullName || 'Unknown',
              invoiceNumber: invoice.invoiceNumber,
              amount: parseFloat(invoice.totalAmount || invoice.amount || 0),
              status: invoice.status || 'pending',
              paymentMethod: invoice.paymentMethod === 'stripe' ? 'Credit Card' :
                invoice.paymentMethod === 'au_becs_debit' ? 'BECS Direct Debit' :
                  invoice.paymentMethod || 'Unknown',
              billingDate: invoice.invoiceDate || invoice.dueDate || invoice.createdAt,
              paymentDate: invoice.paidAt || null,
              type: invoice.billingType === 'pro-rated' ? 'pro-rated' : 'monthly'
            }));
            setBillingHistory(transformedHistory);
            return;
          }
        }
      }

      // Load all recent invoices for billing runs summary
      const allInvoicesResponse = await fetch(API_ENDPOINTS.ADMIN_BILLING_MANAGEMENT, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          action: 'getAllInvoices',
          limit: 100
        })
      });

      if (allInvoicesResponse.ok) {
        const invoicesData = await allInvoicesResponse.json();
        if (invoicesData.success && invoicesData.invoices) {
          const invoices = invoicesData.invoices || [];

          // Group invoices by date for billing runs
          const runsByDate = {};
          invoices.forEach(invoice => {
            const date = invoice.invoiceDate ? new Date(invoice.invoiceDate).toISOString().split('T')[0] : 'unknown';
            if (!runsByDate[date]) {
              runsByDate[date] = {
                invoices: [],
                successful: 0,
                failed: 0,
                pending: 0,
                totalAmount: 0
              };
            }
            runsByDate[date].invoices.push(invoice);
            if (invoice.status === 'paid') runsByDate[date].successful++;
            else if (invoice.status === 'failed') runsByDate[date].failed++;
            else runsByDate[date].pending++;
            runsByDate[date].totalAmount += parseFloat(invoice.totalAmount || invoice.amount || 0);
          });

          // Convert to billing runs format
          const runs = Object.keys(runsByDate)
            .sort((a, b) => new Date(b) - new Date(a))
            .slice(0, 10)
            .map((date, index) => ({
              id: index + 1,
              runDate: new Date(date).toISOString(),
              totalProcessed: runsByDate[date].invoices.length,
              successful: runsByDate[date].successful,
              failed: runsByDate[date].failed,
              pending: runsByDate[date].pending,
              totalAmount: runsByDate[date].totalAmount,
              status: 'completed'
            }));

          setRecentBillingRuns(runs);
        }
      }
    } catch (error) {
      console.error('Error loading billing history:', error);
    }
  };

  // triggerBillingForCustomer was removed — per-customer manual charge button
  // was both rare and dangerous, and the only remaining flow is the guarded
  // "Run Full Billing" header button. If we ever need a per-customer trigger
  // again, restore from git history (commit aad293a era).

  const triggerFullBillingRun = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.ADMIN_FULL_BILLING_RUN, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ testMode: false })
      });

      if (response.ok) {
        const result = await response.json();
        alert(`Billing run completed: ${result.paid} paid, ${result.failed} failed, ${result.pending} pending`);
        loadBillingData();
        loadBillingHistory();
      } else {
        throw new Error('Failed to run billing');
      }
    } catch (error) {
      console.error('Error running billing:', error);
      alert('Failed to run billing. Please try again.');
    }
  };

  const getPaymentMethodIcon = (type) => {
    switch (type) {
      case 'card':
      case 'credit_card':
        return <FaCreditCard className="text-[#83a598]" />;
      case 'becs':
      case 'au_becs_debit':
        return <FaDollarSign className="text-[#b8bb26]" />;
      case 'direct_debit':
        return <FaDollarSign className="text-[#d3869b]" />;
      default:
        return <FaExclamationTriangle className="text-[#928374]" />;
    }
  };

  const getPaymentMethodStatus = (status) => {
    switch (status) {
      case 'active': return 'text-[#b8bb26] bg-[#3c3836] border border-[#458588]';
      case 'failed': return 'text-[#fb4934] bg-[#3c3836] border border-[#cc241d]';
      case 'pending': return 'text-[#fabd2f] bg-[#3c3836] border border-[#d79921]';
      case 'expired': return 'text-[#bdae93] bg-[#3c3836] border border-[#504945]';
      default: return 'text-[#bdae93] bg-[#3c3836] border border-[#504945]';
    }
  };

  const getMandateStatusBadge = (pm) => {
    if (pm.type !== 'au_becs_debit') return null;

    const status = (pm.mandateStatus || pm.status || 'unknown').toLowerCase();
    let classes = 'text-xs px-2 py-0.5 rounded-full font-medium ';
    let label = `Mandate: ${status}`;

    if (status === 'active') {
      classes += 'bg-[#3c3836] text-[#b8bb26] border border-[#458588]';
    } else if (status === 'inactive' || status === 'revoked' || status === 'canceled') {
      classes += 'bg-[#3c3836] text-[#fb4934] border border-[#cc241d]';
    } else {
      classes += 'bg-[#3c3836] text-[#bdae93] border border-[#504945]';
    }

    return (
      <span className={classes}>
        {label}
      </span>
    );
  };

  // Load invoice details
  const handleViewInvoice = async (invoice) => {
    try {
      setInvoiceLoading(true);
      const response = await fetch(API_ENDPOINTS.ADMIN_BILLING_MANAGEMENT, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          action: 'getInvoice',
          invoiceId: invoice.id || invoice.invoiceId,
          invoiceNumber: invoice.invoiceNumber
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setSelectedInvoice(data.invoice);
          setShowInvoiceModal(true);
        }
      } else {
        alert('Failed to load invoice details');
      }
    } catch (error) {
      console.error('Error loading invoice:', error);
      alert('Failed to load invoice details');
    } finally {
      setInvoiceLoading(false);
    }
  };

  // Download invoice (Official PDF from S3/CloudFront)
  const handleDownloadInvoice = () => {
    if (!selectedInvoice) return;

    // Check if the invoice record has a pre-stored PDF URL
    const invPdfUrl = selectedInvoice.invoicePdfUrl || selectedInvoice.invoiceHtmlUrl;
    const invNum = selectedInvoice.invoiceNumber;

    if (!invNum) {
      alert('Invoice number not found');
      return;
    }

    // Determine the best URL
    let downloadUrl = `https://invoice.peaknet.com.au/invoices/${invNum}/${invNum}.pdf`;

    // If the record has a specific S3 path, we could try to map it, 
    // but for simplicity and reliability, we use the standard path.

    console.log('Attempting to download official PDF:', downloadUrl);

    // Create a temporary link to test the URL or just open it
    const win = window.open(downloadUrl, '_blank');

    // If it's an old invoice (no invoicePdfUrl in DB), warn that it might be missing
    if (!selectedInvoice.invoicePdfUrl && !selectedInvoice.invoiceHtmlUrl) {
      console.warn('This is an older invoice. Official PDF might not exist in S3 yet.');
      // We could automatically fall back to client-side here if we had a way to detect 403 instantly,
      // but for now, we'll let the user know they can use the 'View' modal's download button.
    }
  };

  // Fetch payment methods for a customer
  const fetchPaymentMethods = async (customer) => {
    if (!customer?.email) return [];
    try {
      const response = await fetch(API_ENDPOINTS.ADMIN_BILLING_MANAGEMENT, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          action: 'getPaymentMethods',
          customerEmail: customer.email,
          email: customer.email
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          return data.paymentMethods || [];
        }
      }
    } catch (error) {
      console.error('Error fetching payment methods:', error);
    }
    return [];
  };

  // Handle viewing customer details
  const handleViewCustomerDetails = async (customer) => {
    // Set initial customer data
    setSelectedCustomer(customer);
    setShowCustomerModal(true);

    // Fetch and update payment methods
    const paymentMethods = await fetchPaymentMethods(customer);

    setSelectedCustomer(prev => {
      // Only update if we're still viewing the same customer
      if (prev && prev.email === customer.email) {
        return {
          ...prev,
          paymentMethods
        };
      }
      return prev;
    });
  };

  // Open the billing-history modal directly (per-row "View Invoices" entry
  // point). Avoids the previous 3-click drill: Eye → Customer modal →
  // View Billing History button → invoices modal. Selecting a customer +
  // loading their invoices in one shot.
  const handleViewCustomerInvoices = async (customer) => {
    setSelectedCustomer(customer);
    setShowBillingModal(true);
    try {
      const historyResponse = await fetch(API_ENDPOINTS.ADMIN_BILLING_MANAGEMENT, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          action: 'getBillingHistory',
          customerEmail: customer.email,
          email: customer.email,
          limit: 100,
        }),
      });
      if (!historyResponse.ok) return;
      const historyData = await historyResponse.json();
      if (!historyData.success || !historyData.billingHistory) return;
      const transformedHistory = historyData.billingHistory.map((invoice) => ({
        id: invoice.invoiceId || invoice.id,
        customerId: invoice.customerId,
        customerName: historyData.customer?.fullName || customer.fullName || 'Unknown',
        invoiceNumber: invoice.invoiceNumber,
        amount: parseFloat(invoice.totalAmount || invoice.amount || 0),
        status: invoice.status || 'pending',
        paymentMethod: invoice.paymentMethod === 'stripe' ? 'Credit Card'
          : invoice.paymentMethod === 'au_becs_debit' ? 'BECS Direct Debit'
          : invoice.paymentMethod || 'Unknown',
        billingDate: invoice.invoiceDate || invoice.dueDate || invoice.createdAt,
        paymentDate: invoice.paidAt || null,
        type: invoice.billingType === 'pro-rated' ? 'pro-rated' : 'monthly',
        invoicePdfUrl: invoice.invoicePdfUrl,
        invoiceHtmlUrl: invoice.invoiceHtmlUrl,
      }));
      setBillingHistory(transformedHistory);
    } catch (error) {
      console.error('Error loading billing history:', error);
    }
  };

  // Send invoice email
  const handleSendInvoice = async (invoiceHistory) => {
    if (!invoiceHistory || !selectedCustomer) {
      alert('Customer or invoice information is missing');
      return;
    }

    try {
      setSendingInvoice(invoiceHistory.id || invoiceHistory.invoiceNumber);

      // First, get the full invoice details
      const invoiceResponse = await fetch(API_ENDPOINTS.ADMIN_BILLING_MANAGEMENT, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          action: 'getInvoice',
          invoiceId: invoiceHistory.id,
          invoiceNumber: invoiceHistory.invoiceNumber,
          customerEmail: selectedCustomer.email
        })
      });

      if (!invoiceResponse.ok) {
        throw new Error('Failed to fetch invoice details');
      }

      const invoiceData = await invoiceResponse.json();
      if (!invoiceData.success || !invoiceData.invoice) {
        throw new Error('Invoice not found');
      }

      const invoice = invoiceData.invoice;

      // Prepare customer data (we have it from selectedCustomer)
      const customer = {
        email: selectedCustomer.email,
        fullName: selectedCustomer.fullName || `${selectedCustomer.firstName || ''} ${selectedCustomer.lastName || ''}`.trim(),
        accountNumber: selectedCustomer.accountNumber,
        plan: selectedCustomer.plan,
        monthlyFee: selectedCustomer.monthlyFee,
        phone: selectedCustomer.phone,
        address: selectedCustomer.address
      };

      // Get payment ID and method from invoice
      const paymentId = invoice.paymentId || invoice.stripePaymentIntentId || null;
      const paymentMethod = invoice.paymentMethod || invoice.paymentMethodType || 'unknown';

      // Call recurring billing Lambda to send invoice email
      // Note: Ensure API_CONFIG.RECURRING_BILLING points to the correct Recurring Billing Lambda, not Reconciliation!
      const sendResponse = await fetch(API_ENDPOINTS.RECURRING_BILLING, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          action: 'sendInvoiceEmail',
          customer: customer,
          invoice: invoice,
          paymentId: paymentId,
          paymentMethod: paymentMethod
        })
      });

      if (!sendResponse.ok) {
        const errorData = await sendResponse.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to send invoice email');
      }

      const sendData = await sendResponse.json();

      if (sendData.success || sendData.message) {
        alert(`Invoice email sent successfully to ${customer.email}`);
      } else {
        throw new Error('Unexpected response from server');
      }

    } catch (error) {
      console.error('Error sending invoice email:', error);
      alert(`Failed to send invoice email: ${error.message}`);
    } finally {
      setSendingInvoice(null);
    }
  };

  // Set default payment method
  const handleSetDefaultPaymentMethod = async (paymentMethodId) => {
    if (!selectedCustomer) return;

    try {
      setPaymentMethodActionLoading(paymentMethodId);
      const response = await fetch(API_ENDPOINTS.ADMIN_BILLING_MANAGEMENT, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          action: 'setDefaultPaymentMethod',
          customerEmail: selectedCustomer.email,
          email: selectedCustomer.email,
          paymentMethodId
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          alert('Default payment method updated successfully');
          // Reload customer data
          await loadBillingData();
          // Reload payment methods in modal
          if (selectedCustomer) {
            const pmResponse = await fetch(API_ENDPOINTS.ADMIN_BILLING_MANAGEMENT, {
              method: 'POST',
              headers: getHeaders(),
              body: JSON.stringify({
                action: 'getPaymentMethods',
                customerEmail: selectedCustomer.email,
                email: selectedCustomer.email
              })
            });
            if (pmResponse.ok) {
              const pmData = await pmResponse.json();
              if (pmData.success) {
                setSelectedCustomer({
                  ...selectedCustomer,
                  paymentMethods: pmData.paymentMethods,
                  defaultPaymentMethod: pmData.paymentMethods.find(pm => pm.isDefault)
                });
              }
            }
          }
        } else {
          alert(data.error || 'Failed to update default payment method');
        }
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to update default payment method');
      }
    } catch (error) {
      console.error('Error setting default payment method:', error);
      alert('Failed to update default payment method');
    } finally {
      setPaymentMethodActionLoading(null);
    }
  };

  // Remove payment method
  const handleRemovePaymentMethod = async (paymentMethodId) => {
    if (!selectedCustomer) return;

    if (!confirm('Are you sure you want to remove this payment method?')) {
      return;
    }

    try {
      setPaymentMethodActionLoading(paymentMethodId);
      const response = await fetch(API_ENDPOINTS.ADMIN_BILLING_MANAGEMENT, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          action: 'removePaymentMethod',
          customerEmail: selectedCustomer.email,
          email: selectedCustomer.email,
          paymentMethodId
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          alert('Payment method removed successfully');
          // Reload customer data
          await loadBillingData();
          // Reload payment methods in modal
          if (selectedCustomer) {
            const pmResponse = await fetch(API_ENDPOINTS.ADMIN_BILLING_MANAGEMENT, {
              method: 'POST',
              headers: getHeaders(),
              body: JSON.stringify({
                action: 'getPaymentMethods',
                customerEmail: selectedCustomer.email,
                email: selectedCustomer.email
              })
            });
            if (pmResponse.ok) {
              const pmData = await pmResponse.json();
              if (pmData.success) {
                setSelectedCustomer({
                  ...selectedCustomer,
                  paymentMethods: pmData.paymentMethods,
                  defaultPaymentMethod: pmData.paymentMethods.find(pm => pm.isDefault)
                });
              }
            }
          }
        } else {
          alert(data.error || 'Failed to remove payment method');
        }
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to remove payment method');
      }
    } catch (error) {
      console.error('Error removing payment method:', error);
      alert('Failed to remove payment method');
    } finally {
      setPaymentMethodActionLoading(null);
    }
  };

  // Retry failed payment
  const handleRetryPayment = async (invoice) => {
    if (!selectedCustomer) return;

    if (!confirm(`Retry payment for invoice ${invoice.invoiceNumber} ($${invoice.amount.toFixed(2)})?`)) {
      return;
    }

    try {
      setRetryPaymentLoading(invoice.id || invoice.invoiceId);
      const response = await fetch(API_ENDPOINTS.ADMIN_BILLING_MANAGEMENT, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          action: 'retryPayment',
          invoiceId: invoice.id || invoice.invoiceId,
          invoiceNumber: invoice.invoiceNumber,
          customerEmail: selectedCustomer.email,
          email: selectedCustomer.email
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          alert('Payment retry successful!');
          // Reload billing history
          await loadBillingHistory();
          // Reload customer data
          await loadBillingData();
        } else {
          alert(data.error || data.details || 'Payment retry failed');
        }
      } else {
        const data = await response.json();
        alert(data.error || data.details || 'Payment retry failed');
      }
    } catch (error) {
      console.error('Error retrying payment:', error);
      alert('Failed to retry payment');
    } finally {
      setRetryPaymentLoading(null);
    }
  };

  // Void (write off) an invoice that will never be collected — e.g. the customer
  // churned away mid-period. The backend refuses a PAID invoice (that is a Stripe
  // refund, not a void) and refuses one already void, so this only ever offers the
  // action where it is legal. The reason is mandatory: a write-off with no stated
  // reason is unauditable.
  //
  // NB this does NOT stop future billing — the customer keeps their schedule and is
  // billed again next cycle. Cancelling the service is a separate action. And the
  // customer is sent nothing.
  const handleVoidInvoice = async (invoice) => {
    if (!selectedCustomer) return;

    const amount = typeof invoice.amount === 'number' ? invoice.amount.toFixed(2) : invoice.amount;
    const reason = prompt(
      `Void invoice ${invoice.invoiceNumber} ($${amount})?\n\n`
      + `Nothing will be collected for it. This does NOT stop future billing and does NOT email the customer.\n\n`
      + `Reason (required, recorded against the invoice):`
    );
    if (reason === null) return;                 // cancelled the prompt
    if (!reason.trim()) { alert('A reason is required to void an invoice.'); return; }

    try {
      setVoidInvoiceLoading(invoice.id || invoice.invoiceId);
      const response = await fetch(API_ENDPOINTS.ADMIN_BILLING_MANAGEMENT, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          action: 'voidInvoice',
          invoiceId: invoice.id || invoice.invoiceId,
          invoiceNumber: invoice.invoiceNumber,
          customerEmail: selectedCustomer.email,
          email: selectedCustomer.email,
          reason: reason.trim()
        })
      });

      const data = await response.json().catch(() => ({}));
      if (response.ok && data.success) {
        alert(data.message || `Invoice ${invoice.invoiceNumber} voided.`);
        await loadBillingHistory();
        await loadBillingData();
      } else {
        alert(data.error || data.details || 'Failed to void invoice');
      }
    } catch (error) {
      console.error('Error voiding invoice:', error);
      alert('Failed to void invoice');
    } finally {
      setVoidInvoiceLoading(null);
    }
  };

  const getBillingStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-[#b8bb26] bg-[#3c3836] border border-[#458588]';
      case 'pending': return 'text-[#fabd2f] bg-[#3c3836] border border-[#d79921]';
      case 'failed': return 'text-[#fb4934] bg-[#3c3836] border border-[#cc241d]';
      case 'suspended': return 'text-[#fe8019] bg-[#3c3836] border border-[#d79921]';
      default: return 'text-[#bdae93] bg-[#3c3836] border border-[#504945]';
    }
  };

  const filteredCustomers = customers.filter(customer => {
    const fullName = customer.fullName || `${customer.firstName || ''} ${customer.lastName || ''}`.trim() || 'Unknown';
    const email = customer.email || '';
    const accountNumber = customer.accountNumber || '';

    const matchesSearch = fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      accountNumber.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus = statusFilter === 'all' || customer.billingStatus === statusFilter;

    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#fabd2f]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#ebdbb2]">Billing Management</h1>
          <p className="text-[#bdae93]">Manage customer billing, payments, and invoicing</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => { setFullRunConfirmText(''); setShowFullRunConfirm(true); }}
            className="px-4 py-2 bg-[#b8bb26] text-[#1d2021] rounded-lg hover:bg-[#98971a] transition-colors flex items-center space-x-2 font-medium"
          >
            <FaPlay size={16} />
            <span>Run Full Billing</span>
          </button>
          <CsvExportButton
            headers={['Invoice', 'Customer Email', 'Date', 'Subtotal ex-GST', 'GST', 'Total inc-GST', 'Status', 'Paid At', 'Payment Method', 'Type']}
            rows={allInvoices.map(inv => [
              inv.invoiceNumber || inv.invoiceId || '',
              inv.customerId || inv.customerEmail || '',
              inv.invoiceDate ? new Date(inv.invoiceDate).toISOString().slice(0, 10) : '',
              (Number(inv.subtotal) || 0).toFixed(2),
              (Number(inv.tax) || 0).toFixed(2),
              (Number(inv.totalAmount) || inv.amount || 0).toFixed(2),
              inv.status || '',
              inv.paidAt ? new Date(inv.paidAt).toISOString().slice(0, 10) : '',
              inv.paymentMethod || '',
              inv.billingType || '',
            ])}
            filename={`peaknet-all-invoices-${new Date().toISOString().slice(0, 10)}.csv`}
            label="Export All Invoices"
            summary={[
              `Total invoices: ${allInvoices.length}`,
              `Paid: ${allInvoices.filter(i => (i.status || '').toLowerCase() === 'paid').length}`,
              `Generated: ${new Date().toISOString()}`,
              `Use for BAS prep — filter rows by Paid At in your spreadsheet to scope to a quarter.`,
            ]}
            className="px-4 py-2 bg-[#83a598] text-[#1d2021] rounded-lg hover:bg-[#458588] transition-colors flex items-center space-x-2 font-medium"
          />
          <button
            onClick={loadBillingData}
            className="px-4 py-2 bg-[#fabd2f] text-[#1d2021] rounded-lg hover:bg-[#fe8019] transition-colors flex items-center space-x-2 font-medium"
          >
            <FaRedo size={16} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Billing Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-[#282828] rounded-xl shadow-sm border border-[#3c3836] p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[#bdae93]">Monthly Revenue</p>
              <p className="text-3xl font-bold text-[#ebdbb2]">${billingStats.monthlyRevenue.toLocaleString()}</p>
              <p className="text-sm text-[#b8bb26]">Active Services</p>
            </div>
            <div className="w-12 h-12 bg-[#3c3836] rounded-lg flex items-center justify-center">
              <FaDollarSign className="text-[#b8bb26]" size={24} />
            </div>
          </div>
        </div>

        <div className="bg-[#282828] rounded-xl shadow-sm border border-[#3c3836] p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[#bdae93]">DD Success Rate</p>
              <p className="text-3xl font-bold text-[#ebdbb2]">{billingStats.ddSuccessRate.toFixed(1)}%</p>
              <p className="text-sm text-[#83a598]">Payment Success</p>
            </div>
            <div className="w-12 h-12 bg-[#3c3836] rounded-lg flex items-center justify-center">
              <FaCreditCard className="text-[#83a598]" size={24} />
            </div>
          </div>
        </div>

        <div className="bg-[#282828] rounded-xl shadow-sm border border-[#3c3836] p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[#bdae93]">Active Services</p>
              <p className="text-3xl font-bold text-[#ebdbb2]">{billingStats.activeServices}</p>
              <p className="text-sm text-[#d3869b]">Billing Active</p>
            </div>
            <div className="w-12 h-12 bg-[#3c3836] rounded-lg flex items-center justify-center">
              <FaWifi className="text-[#d3869b]" size={24} />
            </div>
          </div>
        </div>

        <div className="bg-[#282828] rounded-xl shadow-sm border border-[#3c3836] p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[#bdae93]">Failed Payments</p>
              <p className="text-3xl font-bold text-[#ebdbb2]">{billingStats.failedPayments}</p>
              <p className="text-sm text-[#fb4934]">Need Attention</p>
            </div>
            <div className="w-12 h-12 bg-[#3c3836] rounded-lg flex items-center justify-center">
              <FaExclamationTriangle className="text-[#fb4934]" size={24} />
            </div>
          </div>
        </div>
      </div>

      {/* Filters — payment-method filter removed: it was decorative (didn't
          filter anything). Re-add once we surface paymentMethodType from the
          server and wire onChange. */}
      <div className="bg-[#282828] rounded-xl shadow-sm border border-[#3c3836] p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <FaSearch className="absolute left-3 top-3 text-[#928374]" />
            <input
              type="text"
              placeholder="Search customers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-[#3c3836] border border-[#504945] rounded-lg text-[#ebdbb2] placeholder-[#928374] focus:ring-2 focus:ring-[#fabd2f] focus:border-transparent"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 bg-[#3c3836] border border-[#504945] rounded-lg text-[#ebdbb2] focus:ring-2 focus:ring-[#fabd2f] focus:border-transparent"
          >
            <option value="all">All Billing Status</option>
            <option value="active">Active</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
            <option value="suspended">Suspended</option>
          </select>
        </div>
      </div>

      {/* Customer Billing List */}
      <div className="bg-[#282828] rounded-xl shadow-sm border border-[#3c3836] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#3c3836]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#bdae93] uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#bdae93] uppercase tracking-wider">
                  Service Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#bdae93] uppercase tracking-wider">
                  Payment Method
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#bdae93] uppercase tracking-wider">
                  Billing Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#bdae93] uppercase tracking-wider">
                  Last Payment
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#bdae93] uppercase tracking-wider">
                  Next Billing
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#bdae93] uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-[#282828] divide-y divide-[#3c3836]">
              {filteredCustomers.map((customer, index) => (
                <tr
                  key={customer.UID || customer.email || customer.accountNumber || `customer-${index}`}
                  className="hover:bg-[#3c3836]"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-gradient-to-r from-[#fabd2f] to-[#fe8019] rounded-full flex items-center justify-center text-[#1d2021] font-bold">
                        {(customer.firstName || customer.fullName || 'U').charAt(0).toUpperCase()}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-[#ebdbb2]">
                          {customer.fullName || `${customer.firstName || ''} ${customer.lastName || ''}`.trim() || 'Unknown Customer'}
                        </div>
                        <div className="text-sm text-[#bdae93]">{customer.email}</div>
                        <div className="text-xs text-[#928374]">{customer.accountNumber}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-[#ebdbb2]">{customer.plan}</div>
                    <div className="text-sm text-[#bdae93]">${customer.monthlyFee}/month</div>
                    <div className="text-xs text-[#928374]">
                      Start: {(() => {
                        const d = customer.serviceStartDate || customer.serviceDate || customer.joinDate;
                        return d ? new Date(d).toLocaleDateString() : 'N/A';
                      })()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      {getPaymentMethodIcon(customer.paymentMethodType)}
                      <div>
                        <div className="text-sm text-[#ebdbb2]">
                          {customer.paymentMethodType === 'card' || customer.paymentMethodType === 'credit_card' ? 'Credit Card' :
                            customer.paymentMethodType === 'becs' || customer.paymentMethodType === 'au_becs_debit' ? 'BECS Direct Debit' :
                              customer.paymentMethodType === 'direct_debit' ? 'Direct Debit' :
                                customer.defaultPaymentMethodId ? 'Payment Method Available' : 'No Payment Method'}
                        </div>
                        {customer.paymentMethods && customer.paymentMethods.length > 1 && (
                          <div className="text-xs text-[#928374]">
                            +{customer.paymentMethods.length - 1} more
                          </div>
                        )}
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getPaymentMethodStatus(customer.paymentMethodStatus)}`}>
                          {customer.paymentMethodStatus}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getBillingStatusColor(customer.billingStatus)}`}>
                      {customer.billingStatus}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-[#ebdbb2]">
                      {customer.lastPaymentAmount > 0 ? `$${customer.lastPaymentAmount}` : 'No payments'}
                    </div>
                    <div className="text-xs text-[#928374]">
                      {customer.lastBillingDate ? new Date(customer.lastBillingDate).toLocaleDateString() : 'Never'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-[#ebdbb2]">
                      {customer.nextBillingDate ? new Date(customer.nextBillingDate).toLocaleDateString() : 'N/A'}
                    </div>
                    <div className="text-xs text-[#928374]">
                      {customer.nextBillingDate ?
                        Math.ceil((new Date(customer.nextBillingDate) - new Date()) / (1000 * 60 * 60 * 24)) + ' days' :
                        'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-3">
                      {/* View Details — customer + payment methods + stats */}
                      <button
                        onClick={() => handleViewCustomerDetails(customer)}
                        className="text-[#fabd2f] hover:text-[#fe8019]"
                        title="View customer billing details + payment methods"
                      >
                        <FaEye size={16} />
                      </button>
                      {/* View Invoices — direct shortcut to the billing-history
                          modal so admin can pull up + view/download/email an
                          invoice in one click instead of drilling through
                          Eye → modal → "View Billing History" button. */}
                      <button
                        onClick={() => handleViewCustomerInvoices(customer)}
                        className="text-[#83a598] hover:text-[#458588]"
                        title="View invoices for this customer"
                      >
                        <FaFileInvoice size={16} />
                      </button>
                      {/* View Billing Summary — comprehensive per-customer
                          overview: outstanding balance, next charge, promo,
                          12mo spending, LTV/forecast/churn, failed payments. */}
                      <button
                        onClick={() => setSummaryCustomer(customer)}
                        className="text-[#b8bb26] hover:text-[#98971a]"
                        title="View billing summary (LTV, forecast, churn, failures)"
                      >
                        <FaChartLine size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent Billing Runs */}
      <div className="bg-[#282828] rounded-xl shadow-sm border border-[#3c3836] p-6">
        <h3 className="text-lg font-semibold text-[#ebdbb2] mb-4">Recent Billing Runs</h3>
        <div className="space-y-4">
          {recentBillingRuns.map((run, index) => (
            <div
              key={run.id || `run-${index}`}
              className="flex items-center justify-between p-4 border border-[#3c3836] rounded-lg bg-[#3c3836]"
            >
              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 bg-gradient-to-r from-[#fabd2f] to-[#fe8019] rounded-full flex items-center justify-center text-[#1d2021] font-bold">
                  B
                </div>
                <div>
                  <p className="font-medium text-[#ebdbb2]">Billing Run #{run.id}</p>
                  <p className="text-sm text-[#bdae93]">
                    {new Date(run.runDate).toLocaleString()} - ${run.totalAmount.toLocaleString()}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="text-right">
                  <div className="flex items-center space-x-2">
                    <span className="text-[#b8bb26] font-medium">{run.successful} paid</span>
                    <span className="text-[#fb4934] font-medium">{run.failed} failed</span>
                    <span className="text-[#fabd2f] font-medium">{run.pending} pending</span>
                  </div>
                  <p className="text-xs text-[#928374]">{run.totalProcessed} total processed</p>
                </div>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${run.status === 'completed' ? 'bg-[#3c3836] text-[#b8bb26] border border-[#458588]' : 'bg-[#3c3836] text-[#fabd2f] border border-[#d79921]'
                  }`}>
                  {run.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Customer Details Modal */}
      {showCustomerModal && selectedCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#282828] rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-[#3c3836]">
            <div className="p-6 border-b border-[#3c3836]">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-[#ebdbb2]">Customer Billing Details</h2>
                <button
                  onClick={() => setShowCustomerModal(false)}
                  className="text-[#928374] hover:text-[#bdae93]"
                >
                  <FaTimes size={24} />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Customer Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-[#ebdbb2] mb-4">Customer Information</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-[#928374]">Full Name</label>
                      <p className="text-[#ebdbb2]">{selectedCustomer.fullName}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#928374]">Email</label>
                      <p className="text-[#ebdbb2]">{selectedCustomer.email}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#928374]">Account Number</label>
                      <p className="text-[#ebdbb2]">{selectedCustomer.accountNumber}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#928374]">Service Start Date</label>
                      <p className="text-[#ebdbb2]">
                        {(() => {
                          const d = selectedCustomer.serviceStartDate || selectedCustomer.serviceDate || selectedCustomer.joinDate;
                          return d ? new Date(d).toLocaleDateString() : 'N/A';
                        })()}
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-[#ebdbb2] mb-4">Billing Information</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-[#928374]">Plan</label>
                      <p className="text-[#ebdbb2]">{selectedCustomer.plan}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#928374]">Monthly Fee</label>
                      <p className="text-[#ebdbb2]">${selectedCustomer.monthlyFee}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#928374]">Billing Status</label>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getBillingStatusColor(selectedCustomer.billingStatus)}`}>
                        {selectedCustomer.billingStatus}
                      </span>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#928374]">Payment Methods</label>
                      <div className="space-y-2">
                        {selectedCustomer.paymentMethods && selectedCustomer.paymentMethods.length > 0 ? (
                          selectedCustomer.paymentMethods.map((pm, index) => (
                            <div key={pm.id || index} className="flex items-center justify-between p-3 bg-[#3c3836] rounded-lg">
                              <div className="flex items-center space-x-3">
                                {getPaymentMethodIcon(pm.type)}
                                <div>
                                  <div className="text-sm font-medium text-[#ebdbb2]">
                                    {pm.type === 'card' || pm.type === 'credit_card' ? 'Credit Card' :
                                      pm.type === 'becs' || pm.type === 'au_becs_debit' ? 'BECS Direct Debit' :
                                        pm.type === 'direct_debit' ? 'Direct Debit' : pm.type || 'Unknown'}
                                  </div>
                                  {pm.type === 'card' && (
                                    <div className="text-xs text-[#928374]">
                                      **** **** **** {pm.last4} ({pm.brand || 'Card'})
                                      {pm.expMonth && pm.expYear && ` - Expires ${pm.expMonth}/${pm.expYear}`}
                                    </div>
                                  )}
                                  {(pm.type === 'becs' || pm.type === 'au_becs_debit') && (
                                    <div className="space-y-1">
                                      <div className="text-xs text-[#928374]">
                                        {pm.bsb && `BSB: ${pm.bsb}`}
                                        {pm.accountNumber && ` | Account: ${pm.accountNumber}`}
                                      </div>
                                      {getMandateStatusBadge(pm)}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                {pm.isDefault && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[#3c3836] text-[#b8bb26] border border-[#458588]">
                                    Default
                                  </span>
                                )}
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getPaymentMethodStatus(pm.status)}`}>
                                  {pm.status}
                                </span>
                                {!pm.isDefault && (
                                  <button
                                    onClick={() => handleSetDefaultPaymentMethod(pm.id)}
                                    disabled={paymentMethodActionLoading === pm.id}
                                    className="text-xs px-2 py-1 bg-[#3c3836] text-[#83a598] rounded hover:bg-[#504945] disabled:opacity-50 border border-[#504945]"
                                    title="Set as default"
                                  >
                                    {paymentMethodActionLoading === pm.id ? '...' : 'Set Default'}
                                  </button>
                                )}
                                <button
                                  onClick={() => handleRemovePaymentMethod(pm.id)}
                                  disabled={paymentMethodActionLoading === pm.id}
                                  className="text-xs px-2 py-1 bg-[#3c3836] text-[#fb4934] rounded hover:bg-[#504945] disabled:opacity-50 border border-[#cc241d]"
                                  title="Remove payment method"
                                >
                                  {paymentMethodActionLoading === pm.id ? '...' : 'Remove'}
                                </button>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="flex items-center space-x-2 text-[#928374]">
                            <FaExclamationTriangle />
                            <span>No payment methods found</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Statistics */}
              <div>
                <h3 className="text-lg font-semibold text-[#ebdbb2] mb-4">Payment Statistics</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-[#3c3836] p-4 rounded-lg border border-[#458588]">
                    <div className="flex items-center space-x-2">
                      <FaCheckCircle className="text-[#b8bb26]" />
                      <span className="font-medium text-[#b8bb26]">Successful Payments</span>
                    </div>
                    <p className="text-2xl font-bold text-[#b8bb26]">{selectedCustomer.successfulPayments || 0}</p>
                  </div>
                  <div className="bg-[#3c3836] p-4 rounded-lg border border-[#cc241d]">
                    <div className="flex items-center space-x-2">
                      <FaTimes className="text-[#fb4934]" />
                      <span className="font-medium text-[#fb4934]">Failed Payments</span>
                    </div>
                    <p className="text-2xl font-bold text-[#fb4934]">{selectedCustomer.failedPayments || 0}</p>
                  </div>
                  <div className="bg-[#3c3836] p-4 rounded-lg border border-[#458588]">
                    <div className="flex items-center space-x-2">
                      <FaFileInvoice className="text-[#83a598]" />
                      <span className="font-medium text-[#83a598]">Total Invoices</span>
                    </div>
                    <p className="text-2xl font-bold text-[#83a598]">{selectedCustomer.totalInvoices || 0}</p>
                  </div>
                </div>
              </div>

              {/* Actions — "Trigger Billing" removed; it was a per-customer
                  manual charge which is dangerous one click away and not a
                  normal workflow. If admin genuinely needs to charge a
                  specific customer now, use the guarded "Run Full Billing"
                  header button (which targets only customers whose
                  nextBillingDate has arrived). */}
              <div className="flex items-center justify-between pt-6 border-t border-[#3c3836]">
                <button
                  onClick={() => {
                    setShowCustomerModal(false);
                    setShowBillingModal(true);
                  }}
                  className="px-4 py-2 bg-[#83a598] text-[#1d2021] rounded-lg hover:bg-[#458588] flex items-center space-x-2"
                >
                  <FaHistory />
                  <span>View Billing History</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Billing History Modal */}
      {showBillingModal && selectedCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#282828] rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-[#3c3836]">
            <div className="p-6 border-b border-[#3c3836]">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <h2 className="text-2xl font-bold text-[#ebdbb2]">Billing History - {selectedCustomer.fullName}</h2>
                <div className="flex items-center gap-2">
                  <CsvExportButton
                    headers={['Invoice', 'Date', 'Amount inc-GST', 'Status', 'Payment Method', 'Type', 'Paid At']}
                    rows={billingHistory.map(h => [
                      h.invoiceNumber || '',
                      h.billingDate ? new Date(h.billingDate).toISOString().slice(0, 10) : '',
                      (Number(h.amount) || 0).toFixed(2),
                      h.status || '',
                      h.paymentMethod || '',
                      h.type || '',
                      h.paymentDate ? new Date(h.paymentDate).toISOString().slice(0, 10) : '',
                    ])}
                    filename={`peaknet-billing-${selectedCustomer.accountNumber || selectedCustomer.email}-${new Date().toISOString().slice(0, 10)}.csv`}
                    label="Export CSV"
                    summary={[
                      `Customer: ${selectedCustomer.fullName} (${selectedCustomer.email})`,
                      `Account: ${selectedCustomer.accountNumber || ''}`,
                      `Total invoices: ${billingHistory.length}`,
                      `Generated: ${new Date().toISOString()}`,
                    ]}
                  />
                  <button
                    onClick={() => setShowBillingModal(false)}
                    className="text-[#928374] hover:text-[#bdae93]"
                  >
                    <FaTimes size={24} />
                  </button>
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="space-y-4">
                {billingHistory.map((history, index) => (
                  <div
                    key={history.id || `history-${index}`}
                    className="flex items-center justify-between p-4 border border-[#3c3836] rounded-lg"
                  >
                    <div className="flex items-center space-x-4 flex-1">
                      <div className="w-10 h-10 bg-gradient-to-r from-[#fabd2f] to-[#fe8019] rounded-full flex items-center justify-center text-[#1d2021] font-bold">
                        I
                      </div>
                      <div>
                        <p className="font-medium text-[#ebdbb2]">{history.invoiceNumber}</p>
                        <p className="text-sm text-[#928374]">
                          {history.paymentMethod} - {new Date(history.billingDate).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="text-right">
                        <p className="font-medium text-[#ebdbb2]">${history.amount.toFixed(2)}</p>
                        <p className="text-sm text-[#928374]">
                          {history.paymentDate ? new Date(history.paymentDate).toLocaleDateString() : 'Not paid'}
                        </p>
                      </div>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${history.status === 'paid' ? 'bg-[#3c3836] text-[#b8bb26] border border-[#458588]' :
                        history.status === 'failed' ? 'bg-[#3c3836] text-[#fb4934] border border-[#cc241d]' :
                          'bg-[#3c3836] text-[#fabd2f] border border-[#d79921]'
                        }`}>
                        {history.status}
                      </span>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleViewInvoice(history)}
                          disabled={invoiceLoading}
                          className="px-3 py-1 text-sm bg-[#3c3836] text-[#83a598] rounded hover:bg-[#504945] disabled:opacity-50 border border-[#504945]"
                          title="View Invoice"
                        >
                          <FaEye className="inline mr-1" />
                          View
                        </button>
                        <button
                          onClick={() => {
                            if (!history.invoicePdfUrl && !history.invoiceHtmlUrl) {
                              alert('This is an older invoice where the official S3 document may not exist yet. Please click "View" and then use the "Download" button inside the invoice view to generate it.');
                            } else {
                              const officialPdfUrl = `https://invoice.peaknet.com.au/invoices/${history.invoiceNumber}/${history.invoiceNumber}.pdf`;
                              window.open(officialPdfUrl, '_blank');
                            }
                          }}
                          className="px-3 py-1 text-sm bg-[#3c3836] text-[#b8bb26] rounded hover:bg-[#504945] border border-[#504945]"
                          title="Download PDF"
                        >
                          <FaDownload className="inline mr-1" />
                          Download
                        </button>
                        <button
                          onClick={() => handleSendInvoice(history)}
                          disabled={sendingInvoice === (history.id || history.invoiceNumber)}
                          className="px-3 py-1 text-sm bg-[#3c3836] text-[#83a598] rounded hover:bg-[#504945] disabled:opacity-50 border border-[#504945]"
                          title="Send Invoice Email"
                        >
                          {sendingInvoice === (history.id || history.invoiceNumber) ? (
                            'Sending...'
                          ) : (
                            <>
                              <FaPaperPlane className="inline mr-1" />
                              Send Invoice
                            </>
                          )}
                        </button>
                        {history.status !== 'paid' && history.status !== 'cancelled' && (
                          <button
                            onClick={() => handleVoidInvoice(history)}
                            disabled={voidInvoiceLoading === history.id}
                            className="px-3 py-1 text-sm bg-[#3c3836] text-[#fb4934] rounded hover:bg-[#504945] disabled:opacity-50 border border-[#cc241d]"
                            title="Void this invoice — nothing will be collected"
                          >
                            {voidInvoiceLoading === history.id ? (
                              'Voiding...'
                            ) : (
                              <>
                                <FaBan className="inline mr-1" />
                                Void
                              </>
                            )}
                          </button>
                        )}
                        {history.status === 'failed' && (
                          <button
                            onClick={() => handleRetryPayment(history)}
                            disabled={retryPaymentLoading === history.id}
                            className="px-3 py-1 text-sm bg-[#3c3836] text-[#fe8019] rounded hover:bg-[#504945] disabled:opacity-50 border border-[#d79921]"
                            title="Retry Payment"
                          >
                            {retryPaymentLoading === history.id ? (
                              'Retrying...'
                            ) : (
                              <>
                                <FaRedo className="inline mr-1" />
                                Retry
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Detail Modal */}
      {showInvoiceModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#282828] rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-[#3c3836]">
            <div className="p-6 border-b border-[#3c3836]">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-[#ebdbb2]">Invoice Details - {selectedInvoice.invoiceNumber}</h2>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleDownloadInvoice}
                    className="px-4 py-2 bg-[#83a598] text-[#1d2021] rounded-lg hover:bg-[#458588] flex items-center space-x-2 font-medium"
                  >
                    <FaDownload />
                    <span>Download</span>
                  </button>
                  <button
                    onClick={() => {
                      setShowInvoiceModal(false);
                      setSelectedInvoice(null);
                    }}
                    className="text-[#928374] hover:text-[#bdae93]"
                  >
                    <FaTimes size={24} />
                  </button>
                </div>
              </div>
            </div>

            <div className="p-6">
              <ProfessionalInvoiceTemplate
                invoice={selectedInvoice}
                customerData={selectedCustomer}
                onClose={() => {
                  setShowInvoiceModal(false);
                  setSelectedInvoice(null);
                }}
                onPrint={() => window.print()}
              />
            </div>
          </div>
        </div>
      )}

      {/* Per-customer Billing Summary modal — comprehensive admin view */}
      {summaryCustomer && (
        <AdminBillingSummary
          customer={summaryCustomer}
          onClose={() => setSummaryCustomer(null)}
        />
      )}

      {/* Run Full Billing typed-confirm modal — guards the scariest button
          in the admin portal. Charges every customer whose nextBillingDate
          has arrived, in real Stripe. Admin must type 'RUN BILLING' exactly. */}
      {showFullRunConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#282828] border border-[#cc241d] rounded-xl max-w-lg w-full p-6">
            <div className="flex items-start gap-3 mb-4">
              <FaExclamationTriangle className="text-[#fb4934] text-2xl mt-1 flex-shrink-0" />
              <div>
                <h2 className="text-xl font-bold text-[#fb4934]">Run Full Billing Cycle</h2>
                <p className="text-sm text-[#bdae93] mt-1">
                  This charges every customer whose next billing date is today or earlier,
                  via real Stripe. Normally this runs automatically at Sydney midnight.
                  Use this button only to recover from a missed scheduled run.
                </p>
              </div>
            </div>
            <label className="block text-sm font-medium text-[#bdae93] mb-2">
              Type <code className="bg-[#1d2021] px-2 py-0.5 rounded text-[#fabd2f] font-mono">RUN BILLING</code> to confirm:
            </label>
            <input
              type="text"
              autoFocus
              value={fullRunConfirmText}
              onChange={(e) => setFullRunConfirmText(e.target.value)}
              placeholder="RUN BILLING"
              className="w-full px-3 py-2 bg-[#1d2021] border border-[#504945] rounded-lg text-[#ebdbb2] focus:ring-2 focus:ring-[#fb4934] focus:border-transparent font-mono mb-4"
            />
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowFullRunConfirm(false)}
                className="px-4 py-2 border border-[#504945] text-[#bdae93] rounded-lg hover:bg-[#3c3836]"
              >
                Cancel
              </button>
              <button
                disabled={fullRunConfirmText !== 'RUN BILLING'}
                onClick={async () => { setShowFullRunConfirm(false); await triggerFullBillingRun(); }}
                className="px-4 py-2 bg-[#fb4934] text-[#1d2021] rounded-lg hover:bg-[#cc241d] font-medium disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Run Full Billing Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingManagement;
